// Public Privacy Policy — HIPAA-aware, references SMS Terms.
import React from "react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <main className="min-h-screen bg-bone py-16 px-4" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto bg-white rounded-xl border border-[#E2E0D8] p-8 sm:p-12 shadow-sm">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue">MedexOVA Medical Transportation</div>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-slate_navy">Privacy Policy</h1>
        <p className="text-xs text-slate-500 mt-2">Last updated: June 2026</p>

        <div className="prose prose-slate mt-8 text-sm leading-relaxed text-slate_navy space-y-4">
          <p>
            Medex Transportation Services LLC ("MedexOVA", "we", "us") respects your privacy. This policy
            describes what information we collect, how we use it, and your rights.
          </p>

          <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">1. Information We Collect</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li><b>Contact information</b> — name, phone, email, facility</li>
            <li><b>Patient information</b> — name, date of birth, mobility type, oxygen / attendant flags</li>
            <li><b>Trip details</b> — pickup/drop-off addresses, dates, times, flight numbers</li>
            <li><b>Payer information</b> — Medicaid ID, prior-auth #, payer type (when applicable)</li>
            <li><b>Location data</b> — GPS pings during active trips (driver portal only)</li>
            <li><b>Communications</b> — SMS and email messages, voice call recordings (via SignalWire)</li>
          </ul>

          <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">2. How We Use Your Information</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Coordinate and dispatch your transportation</li>
            <li>Send trip confirmations, driver assignment, and status updates via SMS and email</li>
            <li>Bill Medicaid, MCO, facility, or private payer as appropriate</li>
            <li>Comply with HIPAA, TCPA, CTIA, and applicable state regulations</li>
          </ul>

          <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">3. SMS &amp; Voice Communications</h2>
          <p>
            By providing your phone number and opting in to SMS notifications, you consent to receive
            transactional SMS messages and automated voice confirmation calls from MedexOVA. See our
            {" "}<Link to="/sms-terms" className="text-brandblue underline font-semibold">SMS Terms &amp; Conditions</Link> for full details.
            <b> Your mobile information will not be sold or shared with third parties for promotional or marketing purposes.</b>
          </p>

          <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">4. Sharing of Information</h2>
          <p>We share information only with parties necessary to fulfill your trip:</p>
          <ul className="list-disc ml-6 space-y-1">
            <li>Internal dispatchers and assigned drivers</li>
            <li>Subcontracted carriers via OVA Dispatch partner network (when MedexOVA capacity is full)</li>
            <li>Medicaid Managed Care Organizations (MCOs) for trip authorization and billing</li>
            <li>Service providers — Brevo (email/SMS), SignalWire (voice), Google Maps (routing), under their respective Business Associate Agreements</li>
          </ul>
          <p className="mt-2">We do not sell your information.</p>

          <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">5. HIPAA</h2>
          <p>
            Patient health information collected for the purpose of arranging non-emergency medical
            transportation is treated as Protected Health Information (PHI) under HIPAA. We maintain
            appropriate administrative, physical, and technical safeguards.
          </p>

          <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">6. Your Rights</h2>
          <ul className="list-disc ml-6 space-y-1">
            <li>Request a copy of your data</li>
            <li>Request deletion (subject to legal retention requirements)</li>
            <li>Opt out of SMS at any time by replying <b>STOP</b></li>
            <li>Opt out of voice confirmation calls by contacting dispatch</li>
          </ul>

          <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">7. Contact</h2>
          <p>
            Medex Transportation Services LLC<br />
            <a href="tel:+18336592699" className="text-brandblue font-semibold">1-833-659-2699</a> · <a href="mailto:info@medexova.com" className="text-brandblue font-semibold">info@medexova.com</a>
          </p>
        </div>

        <div className="mt-12 pt-6 border-t border-[#E2E0D8] flex items-center justify-between">
          <Link to="/" className="text-sm text-brandblue hover:text-brandblue-hover font-semibold">← Back to home</Link>
          <Link to="/sms-terms" className="text-sm text-brandblue hover:text-brandblue-hover font-semibold">SMS Terms →</Link>
        </div>
      </div>
    </main>
  );
}
